import Head from 'next/head';
import { useRouter } from 'next/router';
import { memo, PropsWithChildren } from 'react';

import { isSinglePageLayoutFromQuery } from '../lib/isSinglePageLayout';

import type { WWQueryProps } from '../context/RegistryContext';

const NOINDEX_NOFOLLOW = 'noindex,nofollow';

// Routes kept out of the index even when the website is searchable. The /rsvp
// route is de-indexed separately by RsvpTemplate.
const NOINDEX_NOFOLLOW_ROUTES = ['photos'];

const getCanonical = (slug: string | null | undefined): string =>
  `${process.env['WWS_HOST']}/${slug ?? ''}`;

interface RobotsOptions {
  searchEngineIndexable?: boolean | null | undefined;
  pinProtect?: boolean | null | undefined;
  firstRoute?: string | undefined;
  isSinglePageLayout?: boolean | undefined;
}

// Returns null for indexable pages so that no robots/googlebot tags are rendered at all.
export const getRobotsContent = ({
  searchEngineIndexable,
  pinProtect,
  firstRoute,
  isSinglePageLayout = false,
}: RobotsOptions): string | null => {
  // password-protected websites are never indexed, regardless of searchable settings
  if (pinProtect) {
    return NOINDEX_NOFOLLOW;
  }

  if (!searchEngineIndexable) {
    return NOINDEX_NOFOLLOW;
  }

  // Single-page layouts render every section on the home URL, so there is no
  // separate route to de-index.
  if (isSinglePageLayout) {
    return null;
  }

  return typeof firstRoute !== 'undefined' &&
    NOINDEX_NOFOLLOW_ROUTES.includes(firstRoute)
    ? NOINDEX_NOFOLLOW
    : null;
};

function Seo({ data, children }: WWQueryProps & PropsWithChildren) {
  const { query } = useRouter();

  if (
    !data ||
    data.weddingWebsiteResult?.__typename !== 'WWS_WeddingWebsiteV2'
  ) {
    return null;
  }

  const {
    firstName = '',
    lastName = '',
    fianceFirstName = '',
    fianceLastName = '',
    coverPhotoPathWithCropbox,
    slug,
    searchEngineIndexable,
    pinProtect,
  } = data.weddingWebsiteResult;
  const safeQuery = query || {};
  const { route = [] } = safeQuery;

  let coverPhotoUrl = '';

  if (coverPhotoPathWithCropbox) {
    coverPhotoUrl = coverPhotoPathWithCropbox;
  }

  const coupleNames = `${firstName} ${lastName} and ${fianceFirstName} ${fianceLastName}'s`;
  const description = `Welcome to ${coupleNames} Wedding Website! View photos, directions, registry details and more at The Knot.`;
  const title = `${coupleNames} Wedding Website - The Knot`;

  const canonical = getCanonical(slug);

  const robots = getRobotsContent({
    searchEngineIndexable,
    pinProtect,
    firstRoute: route[0],
    isSinglePageLayout: isSinglePageLayoutFromQuery(data, safeQuery),
  });

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {robots && (
        <>
          <meta name="googlebot" content={robots} />
          <meta name="robots" content={robots} />
        </>
      )}
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={coverPhotoUrl} />
      <meta property="og:type" content="website" />
      {children}
    </Head>
  );
}

// The password page only has a WWS_Error result (no slug or couple info),
// so it builds its metadata from the route and is never indexed.
function PasswordPageSeoComponent({ children }: PropsWithChildren) {
  const { query } = useRouter();
  const slug = Array.isArray(query['slug']) ? query['slug'][0] : query['slug'];
  const canonical = getCanonical(slug);

  return (
    <Head>
      <link rel="canonical" href={canonical} />
      <meta name="googlebot" content={NOINDEX_NOFOLLOW} />
      <meta name="robots" content={NOINDEX_NOFOLLOW} />
      <meta property="og:url" content={canonical} />
      {children}
    </Head>
  );
}

export const PasswordPageSeo = memo(PasswordPageSeoComponent);

export default memo(Seo);
